var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { krBaseCSS } from '../style/base.js';
/**
 * A tabbed navigation component for switching between content sections.
 *
 * @element kr-tab-group
 *
 * @slot - Default slot for kr-tab children
 *
 * @fires tab-change - Fired when the active tab changes, detail: { activeTabId: string }
 * @fires tab-dismiss - Fired when a dismissible tab is closed, detail: { tabId: string }
 *
 * @example
 * ```html
 * <kr-tab-group active-tab-id="settings">
 *   <kr-tab id="home" label="Home">
 *     <svg slot="icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">...</svg>
 *     <p>Home content here</p>
 *   </kr-tab>
 *   <kr-tab id="settings" label="Settings" badge="3">
 *     <svg slot="icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">...</svg>
 *     <p>Settings content here</p>
 *   </kr-tab>
 * </kr-tab-group>
 * ```
 */
export class KRTabGroup extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Whether tabs should stretch to fill the full width evenly
         */
        this.justified = false;
        /**
         * Size of the tabs: 'small' | 'medium' | 'large'
         */
        this.size = 'medium';
    }
    updated(changes) {
        if (changes.has('activeTabId')) {
            this._updateActiveTab();
        }
    }
    firstUpdated() {
        this._updateActiveTab();
    }
    _getTabs() {
        return Array.from(this.querySelectorAll('kr-tab'));
    }
    _updateActiveTab() {
        const tabs = this._getTabs();
        if (tabs.length === 0) {
            return;
        }
        if (!this.activeTabId) {
            this.activeTabId = tabs[0]?.id;
        }
        tabs.forEach((tab) => {
            tab.active = tab.id === this.activeTabId;
        });
        this.requestUpdate();
    }
    _handleTabClick(tab) {
        if (tab.disabled) {
            return;
        }
        this.activeTabId = tab.id;
        this.dispatchEvent(new CustomEvent('tab-change', {
            detail: { activeTabId: tab.id },
            bubbles: true,
            composed: true,
        }));
    }
    _handleTabDismiss(tab, e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('tab-dismiss', {
            detail: { tabId: tab.id },
            bubbles: true,
            composed: true,
        }));
    }
    _handleKeyDown(e) {
        const enabledTabs = this._getTabs().filter((t) => !t.disabled);
        const currentIndex = enabledTabs.findIndex((t) => t.id === this.activeTabId);
        let newIndex = -1;
        switch (e.key) {
            case 'ArrowLeft':
                if (currentIndex > 0) {
                    newIndex = currentIndex - 1;
                }
                else {
                    newIndex = enabledTabs.length - 1;
                }
                break;
            case 'ArrowRight':
                if (currentIndex < enabledTabs.length - 1) {
                    newIndex = currentIndex + 1;
                }
                else {
                    newIndex = 0;
                }
                break;
            case 'Home':
                newIndex = 0;
                break;
            case 'End':
                newIndex = enabledTabs.length - 1;
                break;
        }
        if (newIndex >= 0) {
            e.preventDefault();
            const newTab = enabledTabs[newIndex];
            this.activeTabId = newTab.id;
            this.dispatchEvent(new CustomEvent('tab-change', {
                detail: { activeTabId: newTab.id },
                bubbles: true,
                composed: true,
            }));
            const tabButtons = this.shadowRoot?.querySelectorAll('.label');
            const targetButton = Array.from(tabButtons || []).find((btn) => btn.getAttribute('data-tab-id') === newTab.id);
            targetButton?.focus();
        }
    }
    _renderTabIcon(tab) {
        const iconElement = tab.getIconElement();
        if (!iconElement) {
            return nothing;
        }
        // Clone the icon element to render in the header
        const clonedIcon = iconElement.cloneNode(true);
        clonedIcon.removeAttribute('slot');
        return html `<span class="label-icon">${clonedIcon}</span>`;
    }
    render() {
        return html `
      <div
        class="header"
        role="tablist"
        @keydown=${this._handleKeyDown}
      >
        ${this._getTabs().map((tab) => html `
          <button
            class=${classMap({
            label: true,
            'label--active': tab.id === this.activeTabId,
            'label--justified': this.justified,
        })}
            role="tab"
            data-tab-id=${tab.id}
            aria-selected=${tab.id === this.activeTabId}
            aria-controls=${`panel-${tab.id}`}
            tabindex=${tab.id === this.activeTabId ? 0 : -1}
            ?disabled=${tab.disabled}
            @click=${() => this._handleTabClick(tab)}
          >
            ${this._renderTabIcon(tab)}
            <span>${tab.label}</span>
            ${tab.badge ? html `<span class="label-badge" style=${styleMap({
            backgroundColor: tab.badgeBackground,
            color: tab.badgeColor
        })}>${tab.badge}</span>` : nothing}
            ${tab.dismissible
            ? html `
                <button
                  class="label-dismiss"
                  type="button"
                  aria-label="Close tab"
                  @click=${(e) => this._handleTabDismiss(tab, e)}
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    width="12"
                    height="12"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              `
            : nothing}
          </button>
        `)}
      </div>
      <div
        class="content"
        role="tabpanel"
        aria-labelledby=${this.activeTabId || ''}
      >
        <slot @slotchange=${this._updateActiveTab}></slot>
      </div>
    `;
    }
}
KRTabGroup.styles = [
    krBaseCSS,
    css `
      :host {
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .header {
        height: 44px;
        display: flex;
        flex-shrink: 0;
        align-items: stretch;
        border-bottom: 1px solid #e5e7eb;
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .header::-webkit-scrollbar {
        display: none;
      }

      .label {
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        font-family: inherit;
        font-size: 14px;
        font-weight: 400;
        color: var(--kr-text-muted);
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
        white-space: nowrap;
      }

      .label:hover:not(:disabled) {
        color: var(--kr-text);
      }

      .label:focus-visible {
        outline: 2px solid var(--kr-focus);
        outline-offset: -2px;
      }

      .label--active {
        color: var(--kr-primary);
        border-bottom-color: var(--kr-primary);
      }

      .label:disabled {
        color: var(--kr-text-disabled);
        cursor: not-allowed;
      }

      .label--justified {
        flex: 1;
        justify-content: center;
      }

      :host([size="small"]) .label {
        padding: 8px 16px;
        font-size: 13px;
        gap: 6px;
      }

      :host([size="large"]) .label {
        padding: 16px 20px;
        font-size: 16px;
        gap: 10px;
      }

      .label-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .label-badge {
        font-size: 11px;
        font-weight: 500;
        color: var(--kr-primary-text);
        background-color: var(--kr-primary);
        padding: 2px 8px;
        border-radius: 10px;
        margin-left: 4px;
      }

      .label-dismiss {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        margin-left: 4px;
        padding: 0;
        background: none;
        border: none;
        border-radius: 2px;
        color: var(--kr-text-muted);
        cursor: pointer;
        transition: background-color 0.15s ease, color 0.15s ease;
      }

      .label-dismiss:hover {
        background-color: var(--kr-border);
        color: var(--kr-text);
      }

      .label-dismiss:focus-visible {
        outline: 2px solid var(--kr-focus);
        outline-offset: 2px;
      }


      .content {
        flex: 1;
        overflow: auto;
      }

      /* Default slot styles - kr-tab children show/hide via their own active attribute */
      ::slotted(kr-tab) {
        display: none;
      }

      ::slotted(kr-tab[active]) {
        display: block;
      }
    `,
];
__decorate([
    property({
        type: String,
        attribute: 'active-tab-id',
    })
], KRTabGroup.prototype, "activeTabId", void 0);
__decorate([
    property({ type: Boolean })
], KRTabGroup.prototype, "justified", void 0);
__decorate([
    property({
        type: String,
        reflect: true,
    })
], KRTabGroup.prototype, "size", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-tab-group')) {
    customElements.define('kr-tab-group', KRTabGroup);
}
//# sourceMappingURL=tabs.js.map